import React from 'react'
import PropTypes from 'prop-types'
import { commodityCodePath } from 'components/routes/api'
import { api } from 'components/api'
import FamilySelector, { Family } from './Family'
import CheckboxPartialSelected from 'images/supplier_commodity_picker/checkbox-partial-selected.svg'
import CheckboxSelected from 'images/supplier_commodity_picker/checkbox-selected.svg'
import SegmentCheckboxUnchecked from 'images/supplier_commodity_picker/segment-checkbox-unchecked.svg'
import CollapseLarge from 'images/supplier_commodity_picker/collapse-large.svg'
import ExpandLarge from 'images/supplier_commodity_picker/expand-large.svg'
import Focusable from './Focusable'
import { Commodity } from 'components/commodity_code_picker/picker/CommodityClass'

export interface Segment {
  id: number
  code: string
  families: Family[]
  all_families_selected: boolean
  item_description: string
}

export interface SegmentComponentProps {
  segment: Segment
  expanded: boolean
  selectedFamilies?: Family[]
  setActiveCode: (code: string | null) => void
  setExpandedSegmentCode: (code: string | null) => void
  isActiveCode: (props: { id: string }) => boolean
  isInActiveCodeHierarchy: (props: { id: string }) => boolean
  toggleSegmentSelected: (hi: boolean, hello: boolean) => void
  updateSelectedSegment: (segment: Segment) => void
  selectSegment: (segment: Segment) => void
  deselectSegment: (segment: Segment) => void
}

interface SegmentComponentState {
  families: Family[]
  expandedFamilyCode: string | null
}

class SegmentComponent extends React.Component<SegmentComponentProps, SegmentComponentState> {
  ref: React.RefObject<HTMLDivElement>
  constructor (props: SegmentComponentProps) {
    super(props)

    this.ref = React.createRef<HTMLDivElement>()
    this.state = {
      families: [],
      expandedFamilyCode: null
    }
  }

  segment = (): Segment => this.props.segment
  isExpanded = (): boolean => this.props.expanded
  selectedFamilies = (): undefined | Family[] => this.props.selectedFamilies
  loadedFamilies = (): Family[] => this.state.families

  handleToggleSegment = (): void => {
    this.isExpanded() ? this.collapse() : this.lazyLoadFamilies().then(() => { this.expand() }).catch(e => console.error(e))
  }

  expand = (): void => {
    if (!this.isExpanded()) {
      this.props.setExpandedSegmentCode(this.segment().code)
      this.scrollToTop()
    }
  }

  collapse = (): void => {
    this.props.setExpandedSegmentCode(null)
    this.props.setActiveCode(null)
  }

  lazyLoadFamilies = async (): Promise<void> => {
    if (this.loadedFamilies().length > 0) { return await Promise.resolve() }

    return await api.get<unknown, Family[]>({
      url: commodityCodePath(this.segment().id),
      headers: {
        Accept: 'application/json'
      }
    }).then(response => {
      this.setState({
        families: response.data
      })
    })
  }

  setExpandedFamilyCode = (code: string | null): void => {
    this.setState({ expandedFamilyCode: code })
  }

  handleSelect = (family: Family, shouldRemove: boolean): void => {
    const updatedSegment = this.segment()
    const selectedFamilies = this.selectedFamilies()

    if (shouldRemove) {
      updatedSegment.families = selectedFamilies?.filter(selectedFamily => selectedFamily.id !== family.id) ?? []
      if (updatedSegment.families.length === 0) {
        this.props.deselectSegment(this.segment())
      } else {
        this.props.updateSelectedSegment({ ...updatedSegment, all_families_selected: false })
      }
      return
    }

    if (selectedFamilies?.some(selectedFamily => selectedFamily.id === family.id) === true) {
      updatedSegment.families = selectedFamilies.map(selectedFamily => {
        return selectedFamily.id === family.id ? family : selectedFamily
      })
    } else {
      updatedSegment.families = [...(selectedFamilies ?? []), family]
    }

    this.props.updateSelectedSegment({
      ...updatedSegment,
      all_families_selected: updatedSegment.families.length === this.loadedFamilies().length &&
        !updatedSegment.families.some((family) => !family.all_classes_selected)
    })
  }

  handleSegmentSelect = (): void => {
    if (this.segment().all_families_selected) {
      this.props.deselectSegment(this.segment())
    } else {
      api.get<unknown, Segment>({
        url: `${commodityCodePath(this.segment().id)}?full_tree_selected=true`,
        headers: {
          Accept: 'application/json'
        }
      })
        .then(response => {
          const updatedSegment = response.data
          this.props.selectSegment(updatedSegment)
          this.setState({
            families: updatedSegment.families
          })
        })
        .then(() => {
          this.expand()
        })
        .catch(e => console.error(e))
    }
  }

  scrollToTop = (): void => {
    const ref = this.ref.current
    if (ref === null) { return }

    const topNav = window.document.querySelector('.top-nav')
    if (topNav === null) return

    const headerOffset = topNav.getBoundingClientRect().bottom
    window.scroll({ top: ref.getBoundingClientRect().top + window.pageYOffset - headerOffset })
  }

  selectedTree = (family: Family): Commodity[] => {
    let selectedFamily: Family | null = null

    const selectedFamilies = this.selectedFamilies()
    if (selectedFamilies !== undefined && selectedFamilies.length > 0) {
      selectedFamily = selectedFamilies.find(treeFamily => treeFamily.id === family.id) ?? null
    }

    if (selectedFamily !== null) {
      return selectedFamily.commodities
    } else {
      return []
    }
  }

  descriptionId = (): string => {
    return `code-${this.segment().code}-description`
  }

  isSelected = (): boolean => {
    return this.segment().all_families_selected
  }

  isPartiallySelected = (): boolean => {
    const selectedFamilies = this.selectedFamilies()
    return !this.segment().all_families_selected && selectedFamilies !== undefined && selectedFamilies.length > 0
  }

  ariaChecked = (): boolean | 'mixed' => {
    if (this.isSelected()) {
      return true
    } else if (this.isPartiallySelected()) {
      return 'mixed'
    } else {
      return false
    }
  }

  renderCheckboxImage = (): React.ReactElement => {
    if (this.isSelected()) {
      return <img src={CheckboxSelected} />
    } else if (this.isPartiallySelected()) {
      return <img src={CheckboxPartialSelected} />
    } else {
      return <img src={SegmentCheckboxUnchecked} />
    }
  }

  renderExpandedBox = (): React.ReactElement => {
    if (this.isExpanded()) {
      return <img src={CollapseLarge} alt='Collapse' />
    } else {
      return <img src={ExpandLarge} alt='Expand' />
    }
  }

  familyOrSelectedFamily = (family: Family): Family => {
    return this.selectedFamilies()?.find(sf => sf.id === family.id) ?? family
  }

  render (): React.ReactElement {
    return (
      <Focusable
        clearFocus={() => this.props.setActiveCode(null)}
        itemId={this.segment().id.toString()}
        isActiveItem={this.props.isActiveCode}
        isInActiveItemHierarchy={this.props.isInActiveCodeHierarchy}
        expand={() => {
          this.lazyLoadFamilies().then(() => this.expand()).catch(e => console.error(e))
        }}
      >
        {({ Checkbox, Highlightable }) => {
          return (
            <div className='commodity-code-selector-item segment' ref={this.ref} data-testid={`code-${this.segment().code}`}>
              <Highlightable>
                <div className='grid-x grid-margin-x align-middle'>
                  <div className='shrink cell'>
                    <Checkbox
                      handleSelect={this.handleSegmentSelect} /* eslint-disable-line react/jsx-handler-names */
                      attributes={{
                        'data-testid': `code-${this.segment().code}-checkbox`,
                        'aria-labelledby': this.descriptionId(),
                        'aria-checked': this.ariaChecked()
                      }}
                    >
                      {this.renderCheckboxImage()}
                    </Checkbox>
                  </div>
                  <div className='shrink cell'>
                    <div className='code'>SEGMENT {this.segment().code}</div>
                  </div>
                  <div className='description auto cell'>
                    <span id={`code-${this.segment().code}-description`} className='title'>{this.segment().item_description}</span>
                  </div>
                  <div className='expand-toggle shrink cell'>
                    <button onClick={this.handleToggleSegment}>
                      {this.renderExpandedBox()}
                    </button>
                  </div>
                </div>
              </Highlightable>
              {this.isExpanded() ? <hr /> : null}
              {this.isExpanded()
                ? this.loadedFamilies().map(family =>
                  <FamilySelector
                    family={this.familyOrSelectedFamily(family)}
                    key={family.code}
                    expanded={this.state.expandedFamilyCode === family.code}
                    setExpandedFamilyCode={this.setExpandedFamilyCode}
                    onSelect={this.handleSelect}
                    setActiveCode={this.props.setActiveCode}
                    isActiveCode={this.props.isActiveCode}
                    isInActiveCodeHierarchy={this.props.isInActiveCodeHierarchy}
                    selectedCommodities={this.selectedTree(family)}
                  />
                )
                : null}
            </div>
          )
        }}
      </Focusable>
    )
  }
}

(SegmentComponent as any).propTypes = {
  segment: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  selectedFamilies: PropTypes.array,
  setActiveCode: PropTypes.func.isRequired,
  setExpandedSegmentCode: PropTypes.func.isRequired,
  isActiveCode: PropTypes.func.isRequired,
  isInActiveCodeHierarchy: PropTypes.func.isRequired,
  toggleSegmentSelected: PropTypes.func.isRequired,
  updateSelectedSegment: PropTypes.func.isRequired,
  selectSegment: PropTypes.func.isRequired,
  deselectSegment: PropTypes.func.isRequired
}

export default SegmentComponent
